import { createActionGroup, emptyProps, props } from '@ngrx/store';
import {
  AccountDto,
  AccountPostPayload,
  TenantAssignment,
  CreateGroupPayload,
  UpdateGroupPayload,
  FeatureDetailDto,
  GrantPermissionPayload,
  GroupDto,
  InvitationDto,
  PermissionDto,
  SendInvitationPayload,
} from './models/rbac.model';

export const RbacActions = createActionGroup({
  source: 'RBAC',
  events: {
    // ── Context ────────────────────────────────────────────────────────────
    'Set Tenant': props<{ tenantUuid: string }>(),
    Clear: emptyProps(),

    // ── Groups ─────────────────────────────────────────────────────────────
    'Load Groups': props<{ tenantUuid?: string; q?: string; groupTypes?: string[] }>(),
    'Load Groups Success': props<{ groups: readonly GroupDto[] }>(),
    'Load Groups Failure': props<{ error: unknown }>(),

    'Create Group': props<{ payload: CreateGroupPayload }>(),
    'Create Group Success': props<{ group: GroupDto }>(),
    'Create Group Failure': props<{ error: unknown }>(),

    'Update Group': props<{
      groupName: string;
      payload: UpdateGroupPayload;
      tenantUuid?: string;
    }>(),
    'Update Group Success': props<{ group: GroupDto }>(),
    'Update Group Failure': props<{ error: unknown }>(),

    'Delete Group': props<{ groupName: string; groupUuid: string; tenantUuid?: string }>(),
    'Delete Group Success': props<{ groupUuid: string }>(),
    'Delete Group Failure': props<{ error: unknown }>(),

    'Assign Group Member': props<{
      groupName: string;
      accountEmail: string;
      tenantUuid?: string;
    }>(),
    'Assign Group Member Success': emptyProps(),
    'Assign Group Member Failure': props<{ error: unknown }>(),

    'Remove Group Member': props<{
      groupName: string;
      accountEmail: string;
      tenantUuid?: string;
    }>(),
    'Remove Group Member Success': emptyProps(),
    'Remove Group Member Failure': props<{ error: unknown }>(),

    'Set Groups Query': props<{ query: string }>(),
    'Set Groups Filters': props<{ filters: Record<string, unknown[]> }>(),

    // ── Accounts ───────────────────────────────────────────────────────────
    'Load Accounts': props<{
      tenantUuid?: string;
      search?: string;
      filters?: Record<string, unknown[]>;
      page?: number;
      size?: number;
    }>(),
    'Load Accounts Success': props<{
      accounts: readonly AccountDto[];
      totalElements: number;
      page: number;
    }>(),
    'Load Accounts Failure': props<{ error: unknown }>(),

    'Create Account': props<{
      payload: AccountPostPayload;
      tenantAssignments?: TenantAssignment[];
    }>(),
    'Create Account Success': props<{ account: AccountDto }>(),
    'Create Account Failure': props<{ error: unknown }>(),

    'Update Account': props<{
      accountUuid: string;
      payload: AccountPostPayload;
      tenantAssignments?: TenantAssignment[];
    }>(),
    'Update Account Success': props<{ account: AccountDto }>(),
    'Update Account Failure': props<{ error: unknown }>(),

    'Delete Account': props<{ accountUuid: string }>(),
    'Delete Account Success': props<{ accountUuid: string }>(),
    'Delete Account Failure': props<{ error: unknown }>(),

    'Reset Account Password': props<{ accountUuid: string }>(),
    'Reset Account Password Success': emptyProps(),
    'Reset Account Password Failure': props<{ error: unknown }>(),

    'Block Account': props<{ accountUuid: string }>(),
    'Block Account Success': props<{ accountUuid: string }>(),
    'Block Account Failure': props<{ error: unknown }>(),

    'Unblock Account': props<{ accountUuid: string }>(),
    'Unblock Account Success': props<{ accountUuid: string }>(),
    'Unblock Account Failure': props<{ error: unknown }>(),

    'Set Accounts Query': props<{ query: string }>(),
    'Set Accounts Filters': props<{ filters: Record<string, unknown[]> }>(),

    // ── Feature Details ────────────────────────────────────────────────────
    'Load Feature Details': emptyProps(),
    'Load Feature Details Success': props<{ featureDetails: readonly FeatureDetailDto[] }>(),
    'Load Feature Details Failure': props<{ error: unknown }>(),

    // ── Permissions ────────────────────────────────────────────────────────
    'Load Permissions': props<{ tenantUuid: string; accountUuid?: string }>(),
    'Load Permissions Success': props<{ permissions: readonly PermissionDto[] }>(),
    'Load Permissions Failure': props<{ error: unknown }>(),

    'Grant Permission': props<{ tenantUuid: string; payload: GrantPermissionPayload }>(),
    'Grant Permission Success': props<{ permission: PermissionDto }>(),
    'Grant Permission Failure': props<{ error: unknown }>(),

    'Revoke Permission': props<{
      tenantUuid: string;
      accountUuid: string;
      featureDetailName: string;
      itemUuid: string;
    }>(),
    'Revoke Permission Success': props<{
      accountUuid: string;
      featureDetailName: string;
      itemUuid: string;
    }>(),
    'Revoke Permission Failure': props<{ error: unknown }>(),

    // ── Invitations ────────────────────────────────────────────────────────
    'Load Invitations': props<{ tenantUuid: string }>(),
    'Load Invitations Success': props<{ invitations: readonly InvitationDto[] }>(),
    'Load Invitations Failure': props<{ error: unknown }>(),

    'Send Invitation': props<{ tenantUuid: string; payload: SendInvitationPayload }>(),
    'Send Invitation Success': props<{ invitation: InvitationDto }>(),
    'Send Invitation Failure': props<{ error: unknown }>(),

    'Revoke Invitation': props<{ tenantUuid: string; invitationId: number }>(),
    'Revoke Invitation Success': props<{ invitationId: number }>(),
    'Revoke Invitation Failure': props<{ error: unknown }>(),
  },
});
